import { Component, Inject, OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ConfirmationModalService } from "@airnauts/ng-components/confirmation-modal-content";
import { DeleteUserGQL, UserDetailsFragment, UserDetailsGQL } from "@/core/graphql/graphql";
import { CosmosDialogService, CosmosDialogStyle } from "@/core/services/cosmos-dialog.service";
import { catchError, filter, first, switchMap, tap } from "rxjs/operators";
import { Subscription } from "rxjs";
import { AccordionItem } from "@/shared/components/accordion/expandable-panel.component";
import {
  EditUserModalComponent,
} from "@/modules/users/components/user-details-modal/components/edit-user-modal/edit-user-modal.component";
import { ConfirmDialogComponent } from "@/shared/components/confirm-dialog/confirm-dialog.component";
import { ConfirmDialogModel } from "@/shared/components/confirm-dialog/confirm-dialog.model";
import { ToastService, ToastType } from "@/core/services/toast.service";
import { UserDialog } from "@/modules/users/components/user-details-modal/user-dialog.model";

@Component({
  selector: 'cosmos-user-summary',
  templateUrl: './user-summary.component.html',
  styleUrls: ['./user-summary.component.scss'],
})
export class UserSummaryComponent implements OnDestroy {
  @ViewChild('editDetails') public editDetails: TemplateRef<any>;
  @ViewChild('deleteUserTemplate') public deleteUserTemplate: TemplateRef<any>;
  @ViewChild('email') public email: TemplateRef<any>;

  public _user: UserDetailsFragment;
  public _accordionData: AccordionItem[];
  private readonly _sub = new Subscription();

  public constructor(
    private readonly _modalRef: MatDialogRef<UserSummaryComponent>,
    private readonly _confirmationModalService: ConfirmationModalService,
    private readonly _userDetailsGql: UserDetailsGQL,
    private readonly _deleteUserGql: DeleteUserGQL,
    private readonly _dialog: CosmosDialogService,
    private readonly _toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) private readonly _data: { userId: number },
  ) {
    this.fetchData();
  }

  public editUser(): void {
    const dialogRef = this._dialog.open<EditUserModalComponent, { user: UserDetailsFragment }>(EditUserModalComponent, {
      data: {
        user: this._user,
      },
      style: CosmosDialogStyle.Gray,
    });

    this._sub.add(dialogRef.afterClosed().pipe(
      filter(data => !!data),
    ).subscribe(_ => {
      this._toastService.raiseMessage("User details had been changed!", ToastType.Success);
      this.fetchData();
    }));
  }

  public fetchData(): void {
    this._sub.add(this._userDetailsGql.fetch({ id: this._data.userId }, {
      errorPolicy: 'all',
    }).pipe(
      tap(x => {
        this._user = x.data.fullUser;
      }),
      catchError(async () => this._modalRef.close()),
    ).subscribe((_) => {
      this.setupAccordionData();
    }));
  }

  public setupAccordionData(): void {
    const date = this.formatSubscriptionLabel();
    this._accordionData = [
      { name: 'Name:', value: this._user.name },
      { name: 'Username:', value: this._user.username ? this._user.username : 'No Data' },
      { name: 'Premium:', value: this._user.isPremium },
      { name: 'Email:', value: this._user.email, template: this.email },
      { name: 'Id:', value: this._user.id },
      { name: 'Recently Edited Cluster:', value: this._user.recentlyEditedCluster?.id },
      { name: 'Subscription:', value: date },
      { name: '', value: '' },
      { name: 'editDetails', value: '', template: this.editDetails },
      { name: 'deleteUser', value: '', template: this.deleteUserTemplate },
    ];
  }

  private formatSubscriptionLabel(): string {
    const dateTime: string = this._user.subscription?.subscriptionValidTo;
    return dateTime ? dateTime.slice(0,10) : 'None';
  }

  public deleteUser(): void {
    const data: UserDialog = {
      refreshData: true,
    };

    const dialogRef = this._dialog.open<ConfirmDialogComponent, ConfirmDialogModel>(
      ConfirmDialogComponent,
      {
        data: {
          dialogMessage: `Would you like to delete this user?`,
          confirmText: 'Confirm',
          cancelText: 'Cancel',
        },
        style: CosmosDialogStyle.Gray,
      });

    dialogRef.afterClosed().pipe(
      first(),
      filter(data => data),
      switchMap(() => {
        return this._deleteUserGql.mutate({ id: this._user.id });
      }),
    ).subscribe(() => {
      this._modalRef.close(data);
      this._toastService.raiseMessage("User has been deleted", ToastType.Warning );
    });
  }

  public showCopyToastMessage(): void {
    this._toastService.raiseMessage("Email address has been copied to clipboard", ToastType.Success );
  }

  public ngOnDestroy(): void {
    this._sub.unsubscribe();
  }
}
